import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Activity, Database, Users, Search, DollarSign, Heart,
  CheckCircle2, AlertTriangle, XCircle, RefreshCw, ArrowUpRight, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';
import { toast } from 'sonner';

const ICONS = { database: Database, users: Users, search: Search, dollar: DollarSign, activity: Activity };
const STATUS_STYLES = {
  pass: { icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  warn: { icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  fail: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
};

export const AdminSiteAuditTab = () => {
  const { adminKey } = useAdmin();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/site-audit', { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) setAudit(await res.json());
    } catch { }
    setLoading(false);
  };

  const sendProfileEmails = async () => {
    setSendingEmails(true);
    try {
      const res = await fetch('/api/admin/send-profile-emails', { method: 'POST', headers: { 'X-Admin-Key': adminKey } });
      const data = await res.json();
      if (data.status === 'success') {
        toast.success(`Sent ${data.sent} emails (${data.skipped} already had profiles)`);
      } else {
        toast.error(data.message || 'Failed to send emails');
      }
    } catch {
      toast.error('Failed to send emails');
    }
    setSendingEmails(false);
  };

  useEffect(() => { runAudit(); }, []);

  if (!audit && !loading) return null;

  const scoreColor = (audit?.percentage || 0) >= 70 ? 'text-green-400' : (audit?.percentage || 0) >= 40 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="space-y-6" data-testid="site-audit-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Site Effectiveness Audit</h2>
          <p className="text-sm text-muted-foreground">Analyzes your site's content, engagement, SEO, and monetization</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={sendProfileEmails} disabled={sendingEmails} size="sm" variant="outline" data-testid="send-profile-emails-btn">
            <Mail size={14} className="mr-1" />
            {sendingEmails ? 'Sending...' : 'Email Profile Reminders'}
          </Button>
          <Button onClick={runAudit} disabled={loading} size="sm" variant="outline">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Running...' : 'Re-run Audit'}
          </Button>
        </div>
      </div>

      {loading && !audit ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-32 bg-card animate-pulse rounded-xl" />)}
        </div>
      ) : audit && (
        <>
          {/* Overall Score */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <Card className="border-border bg-card">
              <CardContent className="py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Overall Site Score</p>
                    <p className={`text-5xl font-black ${scoreColor}`}>{audit.percentage}%</p>
                    <p className="text-xs text-muted-foreground mt-1">{audit.score}/{audit.max_score} checks passed</p>
                  </div>
                  <div className="relative w-24 h-24">
                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className="text-border" />
                      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="8" className={scoreColor}
                        strokeDasharray={`${(audit.percentage / 100) * 251.2} 251.2`} strokeLinecap="round" />
                    </svg>
                    <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${scoreColor}`}>{audit.percentage}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {audit.categories.map((cat, ci) => {
              const Icon = ICONS[cat.icon] || Activity;
              const pct = Math.round((cat.score / Math.max(cat.max, 1)) * 100);
              const catColor = pct >= 70 ? 'text-green-400' : pct >= 40 ? 'text-yellow-400' : 'text-red-400';
              return (
                <motion.div key={cat.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ci * 0.05 }}>
                  <Card className="border-border bg-card h-full">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon size={16} className="text-purple-400" />
                          <CardTitle className="text-sm">{cat.name}</CardTitle>
                        </div>
                        <span className={`text-sm font-bold ${catColor}`}>{cat.score}/{cat.max}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {cat.checks.map((check) => {
                        const S = STATUS_STYLES[check.status] || STATUS_STYLES.warn;
                        const SIcon = S.icon;
                        return (
                          <div key={check.name} className={`flex items-start gap-2 p-2 rounded-lg ${S.bg} border ${S.border}`}>
                            <SIcon size={14} className={`${S.color} shrink-0 mt-0.5`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium">{check.name}</span>
                                <span className="text-xs font-mono text-muted-foreground">{check.value}</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{check.tip}</p>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Recommendations */}
          {audit.recommendations && audit.recommendations.length > 0 && (
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <ArrowUpRight size={16} className="text-purple-400" /> Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {audit.recommendations.map((rec, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${rec.priority === 'high' ? 'border-red-500/30 bg-red-500/5' : rec.priority === 'medium' ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-border bg-card/50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${rec.priority === 'high' ? 'bg-red-500/20 text-red-400' : rec.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>{rec.priority}</span>
                      <span className="text-sm font-semibold">{rec.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{rec.description}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};
